import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, MailX, ArrowRight } from "lucide-react";
import { api, setToken, setUser as setStoredUser } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token");
  const code = params.get("code");
  const next = params.get("next") || "/checkout";

  const [state, setState] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");
  const [userName, setUserName] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token || !code) {
      setState("error");
      setMessage("This verification link is missing required parameters. Please open the link exactly as it appears in your email.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email-link", { token, code });
        setToken(data.token);
        setStoredUser(data.user);
        setUser(data.user);
        setUserName(data.user.first_name || data.user.name);
        setState("success");
        toast.success("Email verified — you're signed in!");
        setTimeout(() => nav(next), 2500);
      } catch (e) {
        setState("error");
        setMessage(e?.response?.data?.detail || "We couldn't verify this link. It may have expired or already been used.");
      }
    })();
  }, [token, code, nav, setUser, next]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="verify-email-page">
      <div className="card p-10 max-w-lg w-full text-center">
        {state === "verifying" && (
          <>
            <Loader2 className="mx-auto text-cyan-300 animate-spin" size={56}/>
            <h1 className="text-2xl font-bold text-white mt-6" style={{fontFamily:'Outfit'}}>Verifying your email…</h1>
            <p className="text-slate-400 text-sm mt-2">Hold tight — this usually takes about a second.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={64} data-testid="verify-success"/>
            <h1 className="text-2xl font-bold text-white mt-5" style={{fontFamily:'Outfit'}}>You&apos;re verified{userName ? `, ${userName}` : ""}!</h1>
            <p className="text-slate-300 text-sm mt-2">Redirecting you to complete your order…</p>
            <button
              onClick={() => nav(next)}
              className="btn-primary mt-6 inline-flex items-center gap-2"
              data-testid="verify-continue-btn"
            >
              Continue now <ArrowRight size={14}/>
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto text-rose-400" size={64} data-testid="verify-error"/>
            <h1 className="text-2xl font-bold text-white mt-5" style={{fontFamily:'Outfit'}}>Verification failed</h1>
            <p className="text-slate-300 text-sm mt-2 flex items-start gap-2 justify-center">
              <MailX size={14} className="mt-0.5 shrink-0"/>
              <span>{message}</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-6 justify-center">
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2">Try signing up again</Link>
              <Link to="/login" className="btn-ghost inline-flex items-center gap-2">Sign in instead</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
